"use client";

import { Clock, Facebook, Instagram, Mail, Phone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: Mail,
  label: "info@formexaconstruction.com",
  href: "mailto:Formexaconstruction@gmail.com"
}, {
  icon: Phone,
  label: "+1 236-688-4639",
  href: "tel:+12366884639"
}];
const socials = [{
  icon: Instagram,
  label: "Instagram",
  href: "#"
}, {
  icon: Facebook,
  label: "Facebook",
  href: "#"
}];
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center px-6 py-20">
        <div className="flex size-16 items-center justify-center bg-primary text-white mb-6">
          <Send className="size-8" />
        </div>
        <p className="font-default text-2xl md:text-3xl text-foreground leading-relaxed max-w-md">
          Gracias por contactarnos. Responderemos a su solicitud a la brevedad.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Por favor ingrese su nombre",
      minLength: {
        value: 2,
        message: "Al menos 2 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-gray-500 font-default">
              Nombre Completo
            </FormLabel>
            <FormControl>
              <Input placeholder="Juan Pérez" className="border-0 border-b-2 border-gray-300 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-gray-400" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "El correo es obligatorio",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ingrese un correo válido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-gray-500 font-default">
              Correo Electrónico
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="juan@ejemplo.com" className="border-0 border-b-2 border-gray-300 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-gray-400" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s\-()]{7,}$/,
        message: "Ingrese un número válido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-gray-500 font-default">
              Teléfono
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 236-688-4639" className="border-0 border-b-2 border-gray-300 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-gray-400" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Por favor escriba un mensaje",
      minLength: {
        value: 10,
        message: "Al menos 10 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-widest text-gray-500 font-default">
              Mensaje
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Cuéntenos sobre su proyecto de construcción..." rows={6} className="border-0 border-b-2 border-gray-300 bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-gray-400 resize-none min-h-[150px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-center gap-3 pt-2">
        <Checkbox id="subscribe-letter" />
        <label htmlFor="subscribe-letter" className="text-sm text-gray-600 font-default cursor-pointer leading-none">
          Acepto recibir información de Formexa Construction
        </label>
      </div>

      {submitError && <div className="p-4 border border-red-600/30 bg-red-50">
          <p className="text-red-600 text-sm">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} size="lg" className="px-10 h-12 font-default tracking-wide bg-primary text-white hover:bg-red-700">
          {isSubmitting ? <>Enviando...</> : <>Enviar Mensaje &rarr;</>}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="84" id="hablemos-de-su-proyecto" className="bg-white py-24 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-28 lg:self-start transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-4 mb-8">
              <span className="h-0.5 w-16 bg-primary" />
              <span className="text-xs uppercase tracking-[0.3em] text-primary font-default font-bold">CONTACT</span>
            </div>

            <h2 className="font-default font-bold text-foreground text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8">ASK FOR FREE  ESTIMATE OF YOUR PROJECT</h2>

            

            <ul className="space-y-6 mb-12">
              {contactDetails.map((item, i) => {
              const Icon = item.icon;
              const content = <span className="flex items-start gap-4 group">
                    <span className="flex items-center justify-center size-10 bg-gray-100 text-primary shrink-0 mt-0.5">
                      <Icon className="size-5" strokeWidth={2} />
                    </span>
                    <span className="text-foreground font-default text-base leading-relaxed group-hover:text-primary transition-colors">
                      {item.label}
                    </span>
                  </span>;
              return <li key={i} data-index={i}>
                    {item.href ? <Link to={item.href} className="block">
                        {content}
                      </Link> : content}
                  </li>;
            })}
            </ul>

            
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            
          </div>
        </div>
      </div>
    </section>;
}